import { DirectoryEntry, PublicProfile } from "../../domain/types";

function initials(displayName: string): string {
    return displayName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("");
}

/**
 * The photo, or a stand-in when there is none.
 *
 * A therapist who published no photo gets initials rather than a gap: a card
 * with a hole in it reads as broken rather than as a deliberate choice.
 */
export default function TherapistAvatar({
    therapist,
    size = "md",
}: {
    therapist: Pick<DirectoryEntry | PublicProfile, "display_name" | "photo_url">;
    size?: "md" | "lg";
}) {
    const dimensions = size === "lg" ? "h-20 w-20 text-title-sm" : "h-12 w-12 text-theme-sm";

    if (therapist.photo_url) {
        return (
            <img
                src={therapist.photo_url}
                alt={therapist.display_name}
                className={`${dimensions} shrink-0 rounded-full object-cover`}
                loading="lazy"
            />
        );
    }

    return (
        <span
            aria-hidden="true"
            className={`${dimensions} shrink-0 rounded-full bg-brand-100 dark:bg-brand-900 text-brand-700 dark:text-brand-200 grid place-items-center font-semibold`}
        >
            {initials(therapist.display_name)}
        </span>
    );
}
